/**
 * ProtectedRoute — gate a route on an active tester session.
 *
 * Story: epic-uat-portal-001-8.
 *
 * Redirects an unauthenticated tester to `/login?returnUrl=<deep-link>` so that,
 * after they follow a fresh magic link, they can be returned to where they were
 * headed. A tester who deep-links straight to `/report` or `/board` with no
 * session therefore lands on login — never a blank screen or a crash (plan
 * Review Focus item 1).
 *
 * Mirrors the dao-suite `ProtectedRoute` role (guard + redirect) without the
 * identity-service session-resolution / external-front-door machinery: the UAT
 * portal's session is a synchronous in-memory token, so there is no async
 * "checking…" hold to render.
 */

import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useTesterAuth } from './testerAuthContext';

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthed } = useTesterAuth();
  const location = useLocation();

  if (!isAuthed) {
    const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  return <>{children}</>;
}
